import { Json } from "@redotech/json/json";
import { privacy } from "@redotech/schema-privacy";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

// Message Parts
export interface TextPart extends z.infer<typeof TextPartSchema_> {}
export const TextPartSchema_ = z
  .object({
    type: z.literal("text").meta({ privacy: privacy.enumOrStatus }),
    text: z.string().meta({ privacy: privacy.text.mixed }),
  })
  .meta({ privacy: privacy.structured });
export const TextPartSchema: z.ZodType<TextPart> = TextPartSchema_;

export interface ImagePart extends z.infer<typeof ImagePartSchema_> {}
export const ImagePartSchema_ = z
  .object({
    type: z.literal("image").meta({ privacy: privacy.enumOrStatus }),
    image: z.url().meta({ privacy: privacy.privateUrl }),
    providerOptions: z
      .record(z.string(), z.record(z.string(), z.json()))
      .optional()
      .meta({ privacy: privacy.untypedPayload }),
  })
  .meta({ privacy: privacy.structured });
export const ImagePartSchema: z.ZodType<ImagePart> = ImagePartSchema_;

export interface FilePart extends z.infer<typeof FilePartSchema_> {}
export const FilePartSchema_ = z
  .object({
    type: z.literal("file").meta({ privacy: privacy.enumOrStatus }),
    data: z.url().meta({ privacy: privacy.privateUrl }),
    mediaType: z.string().meta({ privacy: privacy.enumOrStatus }),
    /** Original filename, when the source (e.g. a chat attachment) knows it.
     *  Lets consumers reference the file by name instead of parsing the URL. */
    filename: z.string().optional().meta({ privacy: privacy.filename }),
  })
  .meta({ privacy: privacy.structured });
export const FilePartSchema: z.ZodType<FilePart> = FilePartSchema_;

export interface ToolCallPart extends z.infer<typeof ToolCallPartSchema_> {}
export const ToolCallPartSchema_ = z
  .object({
    type: z.literal("tool-call").meta({ privacy: privacy.enumOrStatus }),
    toolCallId: z.string().meta({ privacy: privacy.id.internal }),
    toolName: z.string().meta({ privacy: privacy.nonPii }),
    input: z
      .json()
      .meta({ privacy: privacy.untypedPayload, annotation: "JSON" }),
  })
  .meta({ privacy: privacy.structured });
export const ToolCallPartSchema: z.ZodType<ToolCallPart> = ToolCallPartSchema_;

export const TextToolResultOutputSchema_ = z.object({
  type: z.literal("text").meta({ privacy: privacy.enumOrStatus }),
  value: z.string().meta({ privacy: privacy.text.mixed }),
});
export interface TextToolResultOutput extends z.infer<
  typeof TextToolResultOutputSchema_
> {}

export const JsonToolResultOutputSchema_ = z.object({
  type: z.literal("json").meta({ privacy: privacy.enumOrStatus }),
  value: z.json().meta({ privacy: privacy.untypedPayload }),
});
export interface JsonToolResultOutput extends z.infer<
  typeof JsonToolResultOutputSchema_
> {}

export const ErrorTextToolResultOutputSchema_ = z.object({
  type: z.literal("error-text").meta({ privacy: privacy.enumOrStatus }),
  value: z.string().meta({ privacy: privacy.systemErrorText }),
});
export interface ErrorTextToolResultOutput extends z.infer<
  typeof ErrorTextToolResultOutputSchema_
> {}

export const ErrorJsonToolResultOutputSchema_ = z.object({
  type: z.literal("error-json").meta({ privacy: privacy.enumOrStatus }),
  value: z.json().meta({ privacy: privacy.untypedPayload }),
});
export interface ErrorJsonToolResultOutput extends z.infer<
  typeof ErrorJsonToolResultOutputSchema_
> {}

export const ToolResultOutputSchema = z
  .union([
    TextToolResultOutputSchema_,
    JsonToolResultOutputSchema_,
    ErrorTextToolResultOutputSchema_,
    ErrorJsonToolResultOutputSchema_,
  ])
  .meta({ privacy: privacy.structured });
export type ToolResultOutput = z.infer<typeof ToolResultOutputSchema>;

/**
 * Extracts the raw value from a structured ToolResultOutput.
 * Use this when you need the underlying value regardless of output type.
 * Returns string | JSONType (which allows property access without casts).
 */
export function extractOutputValue(
  output: ToolResultOutput,
): ToolResultOutput["value"] {
  return output.value;
}

export function createToolResultOutput(
  value: Json,
  isError?: boolean,
): ToolResultOutput {
  if (isError) {
    return typeof value === "string"
      ? { type: "error-text", value }
      : { type: "error-json", value };
  }
  if (typeof value === "string") {
    return { type: "text", value };
  }
  return { type: "json", value };
}

export interface ToolResultPart extends z.infer<typeof ToolResultPartSchema_> {}
export const ToolResultPartSchema_ = z
  .object({
    type: z.literal("tool-result").meta({ privacy: privacy.enumOrStatus }),
    toolCallId: z.string().meta({ privacy: privacy.id.internal }),
    toolName: z.string().meta({ privacy: privacy.nonPii }),
    output: ToolResultOutputSchema.meta({ privacy: privacy.structured }),
    isError: z.boolean().nullish().meta({ privacy: privacy.flag }),
    errorMetadata: z
      .record(z.string(), z.json())
      .nullish()
      .meta({ privacy: privacy.untypedPayload }),
    /** Images the tool returned for the model to look at, alongside `output`. */
    images: z
      .array(ImagePartSchema_)
      .nullish()
      .meta({ privacy: privacy.structured }),
    /**
     * Set when `output` is a view of something that changes. The model is
     * shown `earlierOutput` instead once a later result has the same `key`.
     */
    latestOnly: z
      .object({
        key: z.string().meta({ privacy: privacy.id.internal }),
        earlierOutput: z.json().meta({ privacy: privacy.untypedPayload }),
      })
      .nullish()
      .meta({ privacy: privacy.structured }),
  })
  .meta({ privacy: privacy.structured });
export const ToolResultPartSchema: z.ZodType<ToolResultPart> =
  ToolResultPartSchema_;

export interface ReasoningPart extends z.infer<typeof ReasoningPartSchema_> {}
export const ReasoningPartSchema_ = z
  .object({
    type: z.literal("reasoning").meta({ privacy: privacy.enumOrStatus }),
    text: z.string().meta({ privacy: privacy.text.mixed }),
    signature: z.string().optional().meta({ privacy: privacy.credential }),
  })
  .meta({ privacy: privacy.structured });
export const ReasoningPartSchema: z.ZodType<ReasoningPart> =
  ReasoningPartSchema_;

export const UserMessageContentSchema = z
  .union([TextPartSchema, ImagePartSchema, FilePartSchema])
  .meta({ privacy: privacy.structured });
export type UserMessageContent = z.infer<typeof UserMessageContentSchema>;

export const AssistantMessageContentSchema = z
  .union([TextPartSchema, ToolCallPartSchema, ReasoningPartSchema])
  .meta({ privacy: privacy.structured });
export type AssistantMessageContent = z.infer<
  typeof AssistantMessageContentSchema
>;
// End Message Parts

// Messages
export const SystemMessageSchema_ = z.object({
  id: z.string().nullish().meta({ privacy: privacy.id.internal }),
  externalMessageId: z
    .string()
    .nullish()
    .meta({ privacy: privacy.id.external.resource }),
  role: z.literal("system").meta({ privacy: privacy.enumOrStatus }),
  content: z.string().meta({ privacy: privacy.untypedPayload }),
});
export interface SystemMessage extends z.infer<typeof SystemMessageSchema_> {}
export const SystemMessageSchema: z.ZodType<SystemMessage> =
  SystemMessageSchema_;

export const UserMessageSchema_ = z.object({
  id: z.string().nullish().meta({ privacy: privacy.id.internal }),
  externalMessageId: z
    .string()
    .nullish()
    .meta({ privacy: privacy.id.external.resource }),
  role: z.literal("user").meta({ privacy: privacy.enumOrStatus }),
  content: z
    .union([
      z.string().meta({ privacy: privacy.untypedPayload }),
      z.array(UserMessageContentSchema).meta({ privacy: privacy.structured }),
    ])
    .meta({ privacy: privacy.untypedPayload }),
});
export interface UserMessage extends z.infer<typeof UserMessageSchema_> {}
export const UserMessageSchema: z.ZodType<UserMessage> = UserMessageSchema_;

export const AiProviderMetricsSchema = z.object({
  inputTokens: z.number().nullish().meta({ privacy: privacy.nonPii }),
  nonCachedInputTokens: z.number().nullish().meta({ privacy: privacy.nonPii }),
  outputTokens: z.number().nullish().meta({ privacy: privacy.nonPii }),
  textOutputTokens: z.number().nullish().meta({ privacy: privacy.nonPii }),
  totalTokens: z.number().nullish().meta({ privacy: privacy.nonPii }),
  providerRequestTimeMs: z.number().nullish().meta({ privacy: privacy.nonPii }),
  cacheReadInputTokens: z.number().nullish().meta({ privacy: privacy.nonPii }),
  cacheWriteInputTokens: z.number().nullish().meta({ privacy: privacy.nonPii }),
  reasoningOutputTokens: z.number().nullish().meta({ privacy: privacy.nonPii }),
  webSearchRequests: z.number().nullish().meta({ privacy: privacy.nonPii }),
  estimatedCostUsd: z.number().nullish().meta({ privacy: privacy.money }),
  model: z.string().nullish().meta({ privacy: privacy.enumOrStatus }),
  responseModel: z.string().nullish().meta({ privacy: privacy.enumOrStatus }),
  provider: z.string().nullish().meta({ privacy: privacy.enumOrStatus }),
  group: z.string().nullish().meta({ privacy: privacy.nonPii }),
});
export type AiProviderMetrics = z.infer<typeof AiProviderMetricsSchema>;

export const AssistantMessageSchema_ = z.object({
  id: z.string().nullish().meta({ privacy: privacy.id.internal }),
  externalMessageId: z
    .string()
    .nullish()
    .meta({ privacy: privacy.id.external.resource }),
  role: z.literal("assistant").meta({ privacy: privacy.enumOrStatus }),
  content: z
    .union([
      z.string().meta({ privacy: privacy.untypedPayload }),
      z
        .array(AssistantMessageContentSchema)
        .meta({ privacy: privacy.structured }),
    ])
    .meta({ privacy: privacy.untypedPayload }),
  feedback: createEnum(["positive", "negative"], {
    enumName: "LlmAssistantMessageFeedback",
  })
    .nullish()
    .meta({ privacy: privacy.enumOrStatus }),
  comment: z.string().nullish().meta({ privacy: privacy.text.mixed }),
  usage: AiProviderMetricsSchema.nullish().meta({
    privacy: privacy.structured,
  }),
});
export interface AssistantMessage extends z.infer<
  typeof AssistantMessageSchema_
> {}
export const AssistantMessageSchema: z.ZodType<AssistantMessage> =
  AssistantMessageSchema_;

export const ToolMessageSchema_ = z.object({
  id: z.string().nullish().meta({ privacy: privacy.id.internal }),
  externalMessageId: z
    .string()
    .nullish()
    .meta({ privacy: privacy.id.external.resource }),
  role: z.literal("tool").meta({ privacy: privacy.enumOrStatus }),
  content: z
    .array(ToolResultPartSchema)
    .meta({ privacy: privacy.untypedPayload }),
});
export interface ToolMessage extends z.infer<typeof ToolMessageSchema_> {}
export const ToolMessageSchema: z.ZodType<ToolMessage> = ToolMessageSchema_;

export const MessageSchema_ = z.union([
  SystemMessageSchema,
  UserMessageSchema,
  AssistantMessageSchema,
  ToolMessageSchema,
]);
export type Message = z.infer<typeof MessageSchema_>;
export const MessageSchema: z.ZodType<Message> = MessageSchema_;

export type MessageContent =
  | ToolResultPart
  | AssistantMessageContent
  | FilePart
  | ImagePart;

export function extractJsonToolResultValues(
  messages: readonly Message[],
  toolNames: string | ReadonlySet<string>,
): Json[] {
  const includesToolName = (toolName: string) =>
    typeof toolNames === "string"
      ? toolName === toolNames
      : toolNames.has(toolName);

  return messages.flatMap((message) =>
    message.role !== "tool"
      ? []
      : message.content.flatMap((part) =>
          part.type === "tool-result" &&
          includesToolName(part.toolName) &&
          part.output.type === "json"
            ? [part.output.value]
            : [],
        ),
  );
}

export function toolCallIds(messages: readonly Message[]): Set<string> {
  return new Set(
    messages.flatMap((message) =>
      message.role === "assistant" && Array.isArray(message.content)
        ? message.content.flatMap((part) =>
            part.type === "tool-call" ? [part.toolCallId] : [],
          )
        : [],
    ),
  );
}

/** Pending tools minus those whose call is in `before` but not `after`. */
export function withoutDroppedToolCalls<T extends { readonly id: string }>(
  pendingTools: T[],
  before: readonly Message[],
  after: readonly Message[],
): T[] {
  const kept = toolCallIds(after);
  const dropped = [...toolCallIds(before)].filter((id) => !kept.has(id));
  if (dropped.length === 0) {
    return pendingTools;
  }
  const droppedIds = new Set(dropped);
  return pendingTools.filter((tool) => !droppedIds.has(tool.id));
}

export const MessageRoleSchema = createEnum(
  ["system", "user", "assistant", "tool"],
  { enumName: "MessageRole" },
);
export type MessageRole = z.infer<typeof MessageRoleSchema>;
